<script setup>
/**
 * FormControlCategoryTitle
 * Recursively builds a breadcrumb string for nested categories
 * by traversing parent IDs.
 */
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';

const props = defineProps({
    tableName: {
        type: String,
        required: true
    },
    id: {
        type: Number,
        default: null
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const title = ref('');

// --- Computed ---

/**
 * Retrieves the internal component ID based on the table name.
 */
const componentId = computed(() => store.state.components.componentIds[props.tableName]);

/**
 * Retrieves the specific field name used for 'titles' for this component.
 */
const nameField = computed(() => store.getters['components/nameField'](componentId.value));

// --- Methods ---

/**
 * Recursive function to fetch category data and build the string.
 * @param {number} id - The ID of the current category level.
 * @param {boolean} isParent - Whether we are currently looking at a parent.
 */
const buildCategoryTitle = async (id, isParent = false) => {
    if (!id) return;

    try {
        const item = await store.dispatch('itemData/get', {
            tableName: props.tableName,
            id
        });

        const currentLabel = item[nameField.value];

        if (!isParent) {
            // Initial call (leaf node)
            title.value = currentLabel;
        } else {
            // Parent call (prepend to breadcrumb)
            title.value = `${currentLabel} > ${title.value}`;
        }

        // If there is a parent, recurse upwards
        if (item.parentId) {
            await buildCategoryTitle(item.parentId, true);
        }
    } catch (error) {
        console.error('Error fetching category title path:', error);
    }
};

const init = () => {
    if (props.id) {
        buildCategoryTitle(props.id);
    } else {
        title.value = '';
    }
};

// --- Lifecycle & Watchers ---

onMounted(() => {
    init();
});

watch(() => props.id, (newId) => {
    if (newId) {
        buildCategoryTitle(newId);
    } else {
        title.value = '';
    }
});
</script>

<template>
    <span>{{ title }}</span>
</template>